import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { TRANSLATIONS } from "../config/i18n";

const LanguageContext = createContext(null);
const STORAGE_KEY = "tessa-language";

// Task 25 — five market languages. Variety names/descriptions stay in
// English (client rule); only UI copy is translated.
export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ru", label: "Русский" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "fr", label: "Français" },
];
const CODES = LANGUAGES.map((l) => l.code);
const LOCALES = { en: "en-US", ru: "ru-RU", de: "de-DE", it: "it-IT", fr: "fr-FR" };

function resolve(obj, path) {
  return path.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), obj);
}

function getInitialLanguage() {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return CODES.includes(stored) ? stored : "en";
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(getInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = lang;
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const t = useCallback(
    (key) => {
      const value = resolve(TRANSLATIONS[lang], key);
      if (value === undefined) return resolve(TRANSLATIONS.en, key) ?? key;
      return value;
    },
    [lang]
  );

  const localize = useCallback(
    (record, field, fallback = "") => {
      if (!record) return fallback;
      // Only RU content variants exist in the DB; every other language
      // falls back to the English field (names/descriptions stay English).
      const suffix = lang === "ru" ? "Ru" : "En";
      return record[`${field}${suffix}`] ?? record[field] ?? fallback;
    },
    [lang]
  );

  const locale = LOCALES[lang] || LOCALES.en;

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, localize, locale }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}
